import { useMemo } from 'react';
import PropTypes from 'prop-types';
import ReactFlow, { ReactFlowProvider, Controls, isNode, } from 'react-flow-renderer';
import CustomNodeComponent from './CustomNodeComponent';
import './ProcessesDiagram.scss';
import dagre from 'dagre';

const position = { x: 0, y: 0 };

const ProcessesDiagram = ({ sandboxResult }) => {

    const allowedActions = {
        'StartProcess': 'Process',
        'CreateFile': 'File',
        'OpenWordDocument': 'File',
        'ShellCreatedFile': 'File',
        'WriteTextFile': 'File',
        'WriteBinaryFile': 'File',
        'CopyFile': 'File',
        'MoveFile': 'File',
        'CopyProgram': 'File',
        'MoveProgram': 'File',
        'HttpRequest': 'Network',
        'ExecuteScript': 'Script',
        'ExecuteSchedulerJob': 'Execute',
        'ExecQuery': 'Execute'
    };

    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));

    const nodeWidth = 50;
    const nodeHeight = 70;
    let initialElements = [];

    const icons = {
        'icon-terminal': ['iexplore', 'outlook', 'explorer', 'powershell'],
        'icon-file-word-solid': ['winword'],
    };

    const onLoad = (reactFlowInstance) => {
        return reactFlowInstance.fitView();
    };

    const checkForChildren = (currentItem, lastItem) => {
        let icon;
        Object.keys(icons).forEach((key) => {
            if (currentItem.additionalInformation.Program && icons[key].includes(currentItem.additionalInformation.Program)) {
                icon = key;
            }
            if (currentItem?.action === 'HttpRequest') {
                icon = 'icon-network';
            }
            return icon;
        });

        const node = {
            id: `horizontal-${currentItem.dataUUID}`,
            type: 'special',
            sourcePosition: 'right',
            targetPosition: 'left',
            data: {
                label: allowedActions[currentItem.action],
                text: currentItem.additionalInformation.Program || currentItem.additionalInformation.File || currentItem.additionalInformation.URI,
                icon: icon || 'icon-file-solid'
            },
            position: position,
        };

        const line = {
            id: `horizontal-${currentItem.dataUUID}`,
            source: lastItem.id,
            type: 'smoothstep',
            target: `horizontal-${currentItem.dataUUID}`,
        };

        initialElements.push(node);
        initialElements.push(line);
    };

    const processedElements = useMemo(() => {
        const filtredResults = sandboxResult?.filter((item) => Object.keys(allowedActions).includes(item.action));
        let lastItemPid = null;
        let indexOf = null;
        let lastItem = null;
        let parentId = null;

        filtredResults.map((item, index) => {
            if (item.additionalInformation.ParentPid === '00000000') {
                let icon;
                parentId = item.dataUUID;

                Object.keys(icons).forEach((key) => {
                    if (item.additionalInformation.Program && icons[key].includes(item.additionalInformation.Program)) {
                        icon = key;
                        return icon;
                    }
                });

                const node = {
                    id: `horizontal-${item.dataUUID}`,
                    type: 'special',
                    sourcePosition: 'right',
                    targetPosition: 'left',
                    data: {
                        label: allowedActions[item.action],
                        text: item.additionalInformation.Program,
                        icon: icon || 'icon-file-solid'
                    },
                    position: position,
                };

                initialElements.push(node);
                lastItemPid = item.additionalInformation.Pid;
                indexOf = initialElements.indexOf(node);
            }

            if (item.additionalInformation.ParentPid === lastItemPid) {

                lastItem = initialElements[indexOf];

            } else {

                lastItem = initialElements.slice(-1)[0];
            }

            index < filtredResults.length && item.dataUUID !== parentId && checkForChildren(item, lastItem);

            return initialElements;
        });


        return initialElements;

    }, [sandboxResult]);


    const getLayoutedElements = (elements, direction = 'LR') => {
        dagreGraph.setGraph({ rankdir: direction });

        elements.forEach((el) => {
            if (isNode(el)) {
                dagreGraph.setNode(el.id, { width: nodeWidth, height: nodeHeight });
            } else {
                dagreGraph.setEdge(el.source, el.target);
            }
        });

        dagre.layout(dagreGraph);

        return elements.map((el) => {
            if (isNode(el)) {
                const nodeWithPosition = dagreGraph.node(el.id);
                el.targetPosition = 'left';
                el.sourcePosition = 'right';

                el.position = {
                    x: (nodeWithPosition.x - nodeWidth / 2 + Math.random() / 1000) * 2,
                    y: nodeWithPosition.y - nodeHeight / 2,
                };
            }

            return el;
        });
    };

    const layoutedElements = getLayoutedElements(processedElements);

    const ReacFlowDom = useMemo(() => {
        if (!layoutedElements) {
            return;
        }
        return <ReactFlowProvider>
            <ReactFlow
                elements={layoutedElements}
                connectionLineType="smoothstep"
                onLoad={onLoad}
                nodeTypes={{ special: CustomNodeComponent }}
                nodesDraggable={false}
                nodesConnectable={false}
            />
            <Controls className="controls"
                showInteractive={false}
            />
        </ReactFlowProvider>;
    }, [layoutedElements]);

    return (
        <div className="layoutflow">
            {ReacFlowDom}
        </div>
    );
};

ProcessesDiagram.propTypes = {
    sandboxResult: PropTypes.object,
};

export default ProcessesDiagram;
